import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Loader2, ArrowLeft, Target, GraduationCap, TrendingUp, Building2,
  Award, Star, Briefcase, ChevronRight,
} from "lucide-react";
import {
  fetchTenantEmployee, fetchTenantSkillGap, fetchTenantEmpSales,
  fetchTenantEmpTraining, fetchTenantLibrary, getActiveTenant,
} from "../lib/tenantApi";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";

const BAND_COLORS = { A: "#94a3b8", B: "#64748b", C: "#0ea5e9", D: "#0284c7", E: "#0c4a6e" };

function TopBar({ tenant, tenantSlug }) {
  const brand = tenant?.brand_color || "#0F172A";
  return (
    <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {tenant?.logo_url ? (
            <img src={tenant.logo_url} alt={tenant?.name} className="h-8 max-w-[140px] object-contain" />
          ) : (
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: brand }}>
                {tenant?.name?.charAt(0) || "P"}
              </div>
              <div className="leading-tight">
                <div className="font-display font-semibold text-sm text-slate-900">{tenant?.name}</div>
                <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">Çalışan Görünümü</div>
              </div>
            </div>
          )}
        </div>
        <Link to="/personas" className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-md">
          <ArrowLeft className="w-3.5 h-3.5" /> Personalar
        </Link>
      </div>
    </div>
  );
}

function ScoreRing({ score, label, color = "#0ea5e9" }) {
  const r = 40;
  const c = 2 * Math.PI * r;
  const off = c - (score / 100) * c;
  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <svg width="100" height="100" className="-rotate-90">
          <circle cx="50" cy="50" r={r} stroke="#e2e8f0" strokeWidth="8" fill="none" />
          <circle cx="50" cy="50" r={r} stroke={color} strokeWidth="8" fill="none" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-display font-bold text-2xl text-slate-900">
          %{score}
        </div>
      </div>
      <div className="text-xs text-slate-500 mt-2 text-center">{label}</div>
    </div>
  );
}

function SkillBar({ name, current, required, color = "#0ea5e9" }) {
  const max = Math.max(required, 5);
  return (
    <div data-testid={`skill-bar-${name}`}>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="font-medium text-slate-800 truncate flex-1">{name}</span>
        <span className="text-slate-500 ml-2 font-mono">
          {current}/{required}
          {current >= required ? <span className="text-emerald-600 ml-1">✓</span> : <span className="text-amber-600 ml-1">↑</span>}
        </span>
      </div>
      <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
        {[1, 2, 3, 4, 5].map((l) => (
          <div key={l} className="absolute top-0 bottom-0 w-px bg-white/60" style={{ left: `${(l / max) * 100}%` }} />
        ))}
        <div className="h-full rounded-full" style={{ width: `${(current / max) * 100}%`, background: color }} />
        <div className="absolute top-[-2px] bottom-[-2px] w-0.5 bg-slate-900" style={{ left: `${(required / max) * 100}%` }} />
      </div>
    </div>
  );
}

export default function RetailEmployeeView() {
  const { slug, empId } = useParams();
  const [emp, setEmp] = useState(null);
  const [gap, setGap] = useState(null);
  const [sales, setSales] = useState([]);
  const [training, setTraining] = useState([]);
  const [library, setLibrary] = useState(null);
  const [loading, setLoading] = useState(true);
  const tenant = getActiveTenant();

  useEffect(() => {
    Promise.all([
      fetchTenantEmployee(slug, empId),
      fetchTenantSkillGap(slug, empId).catch(() => null),
      fetchTenantEmpSales(slug, empId).catch(() => []),
      fetchTenantEmpTraining(slug, empId).catch(() => []),
      fetchTenantLibrary(slug).catch(() => null),
    ])
      .then(([e, g, s, t, lib]) => {
        setEmp(e);
        setGap(g);
        setSales(s);
        setTraining(t);
        setLibrary(lib);
      })
      .finally(() => setLoading(false));
  }, [slug, empId]);

  // Find the next career step in the same ladder
  const nextStep = useMemo(() => {
    if (!emp?.role?.ladder_id || !library?.career_ladders) return null;
    const ladder = library.career_ladders.find((l) => l.id === emp.role.ladder_id);
    if (!ladder) return null;
    const idx = ladder.steps.findIndex((s) => s.band === emp.role.career_band);
    return ladder.steps[idx + 1] || null;
  }, [emp, library]);

  const totalTrainingHours = useMemo(
    () => training.reduce((sum, t) => sum + (t.duration_hours || 0), 0),
    [training],
  );
  const avgTrainingScore = useMemo(() => {
    const scored = training.filter((t) => t.score);
    return scored.length ? Math.round(scored.reduce((s, t) => s + t.score, 0) / scored.length) : null;
  }, [training]);
  const avgSalesPct = useMemo(() => {
    const pcts = sales.filter((s) => s.target).map((s) => (s.actual / s.target) * 100);
    return pcts.length ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length) : null;
  }, [sales]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <TopBar tenant={tenant} />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  if (!emp) {
    return (
      <div className="min-h-screen bg-slate-50">
        <TopBar tenant={tenant} />
        <div className="text-center py-20 text-sm text-slate-500">Çalışan bulunamadı</div>
      </div>
    );
  }

  const compIndex = {};
  library?.skill_clusters?.forEach((c) =>
    c.skills?.forEach((s) => {
      compIndex[s.id] = s;
    }),
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar tenant={tenant} />
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Hero */}
        <div className="bg-white border border-slate-200 rounded-2xl p-7 mb-6">
          <div className="flex items-start gap-6">
            <Avatar className="w-24 h-24 ring-2 ring-slate-200">
              <AvatarImage src={emp.avatar} alt={emp.name} />
              <AvatarFallback>{emp.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h1 className="font-display text-3xl font-bold text-slate-900">{emp.name}</h1>
                {emp.is_talent && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.65rem] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                    <Star className="w-3 h-3 fill-amber-500" /> Yetenek Havuzu
                  </span>
                )}
              </div>
              <div className="text-lg text-slate-700 mb-1">{emp.title}</div>
              <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" />{emp.branch_name}</span>
                <span>{emp.region}</span>
                <span>Kıdem: {emp.tenure_years?.toFixed(1)} yıl</span>
                <span>Band <strong className="text-slate-900">{emp.salary_band}</strong></span>
              </div>
            </div>
            <div className="flex gap-6">
              <ScoreRing score={Math.round((emp.performance_rating || 0) * 20)} label="Performans" color="#10b981" />
              {emp.engagement_score ? <ScoreRing score={emp.engagement_score} label="Bağlılık" color="#0ea5e9" /> : null}
            </div>
          </div>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="text-[0.7rem] uppercase tracking-wider text-slate-500 font-semibold mb-1">Hazır olma</div>
            <div className="font-display text-3xl font-bold text-slate-900">%{gap?.readiness_score || 0}</div>
            <div className="text-xs text-slate-500 mt-1">Bir sonraki kademe için</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="text-[0.7rem] uppercase tracking-wider text-slate-500 font-semibold mb-1">Satış Başarısı</div>
            <div className="font-display text-3xl font-bold text-slate-900">{avgSalesPct ? `%${avgSalesPct}` : "—"}</div>
            <div className="text-xs text-slate-500 mt-1">Son {sales.length} dönem ort.</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="text-[0.7rem] uppercase tracking-wider text-slate-500 font-semibold mb-1">Eğitim</div>
            <div className="font-display text-3xl font-bold text-slate-900">{training.length}</div>
            <div className="text-xs text-slate-500 mt-1">{totalTrainingHours.toFixed(0)} saat · {avgTrainingScore ? `Ort. ${avgTrainingScore}` : ""}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="text-[0.7rem] uppercase tracking-wider text-slate-500 font-semibold mb-1">Yetkinlik</div>
            <div className="font-display text-3xl font-bold text-slate-900">{emp.current_skills?.length || 0}</div>
            <div className="text-xs text-slate-500 mt-1">{gap ? `${gap.gaps.length} açık, ${gap.aligned.length} tam` : ""}</div>
          </div>
        </div>

        {/* Career path next step + Skill gap */}
        {gap && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* Skill Gap */}
            <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="skill-gap-panel">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-4 h-4 text-sky-600" />
                <h3 className="font-display font-semibold text-slate-900">Yetkinlik Açıkları</h3>
                <span className="ml-auto text-xs text-slate-500">vs {gap.target_role.title}</span>
              </div>
              <div className="space-y-3">
                {gap.gaps.length === 0 ? (
                  <p className="text-xs text-emerald-600">Tüm hedef seviyelere ulaşılmış.</p>
                ) : (
                  gap.gaps.slice(0, 6).map((g) => (
                    <SkillBar key={g.skill_id} name={g.skill_name || g.skill_id} current={g.current_level} required={g.required_level} color="#f59e0b" />
                  ))
                )}
                {gap.aligned.slice(0, 3).map((a) => (
                  <SkillBar key={a.skill_id} name={a.skill_name || a.skill_id} current={a.current_level} required={a.required_level} color="#10b981" />
                ))}
              </div>
            </div>

            {/* Next career step */}
            <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="career-next-step">
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-4 h-4 text-purple-600" />
                <h3 className="font-display font-semibold text-slate-900">Bir Sonraki Adım</h3>
              </div>
              {nextStep ? (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ background: BAND_COLORS[nextStep.band] || "#0ea5e9" }}
                    >
                      {nextStep.band}
                    </div>
                    <div className="flex-1">
                      <div className="font-display text-lg font-bold text-slate-900">{nextStep.role_label}</div>
                      <div className="text-xs text-slate-500">Beklenen süre: {nextStep.duration || "—"}</div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300" />
                  </div>
                  <div className="text-[0.7rem] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    Gereken Yeni Yetkinlikler
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {nextStep.required_competencies?.slice(0, 8).map((rc) => (
                      <span key={rc.comp_id} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-700">
                        <span className="font-mono text-[0.6rem] text-slate-500">{rc.comp_id}</span>
                        <span className="truncate max-w-[120px]">{compIndex[rc.comp_id]?.name || ""}</span>
                        <span className="text-[0.6rem] text-slate-400">L{rc.target_level}</span>
                      </span>
                    ))}
                  </div>
                  <Link
                    to={`/tenant/${slug}/career-paths`}
                    className="mt-4 inline-flex items-center gap-1.5 text-xs text-sky-600 hover:gap-2 transition-all font-medium"
                  >
                    Tüm kariyer yolunu gör <ArrowLeft className="w-3 h-3 rotate-180" />
                  </Link>
                </>
              ) : (
                <p className="text-xs text-slate-500">Bu kademe için bir sonraki adım tanımlı değil.</p>
              )}
            </div>
          </div>
        )}

        {/* Sales performance + Training history */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="sales-panel">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              <h3 className="font-display font-semibold text-slate-900">Satış Performansı</h3>
              <span className="ml-auto text-xs text-slate-500">{sales.length} dönem</span>
            </div>
            {sales.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Bu çalışan için satış kaydı yok.</p>
            ) : (
              <div className="space-y-2">
                {sales.slice(-8).map((s, i) => {
                  const pct = s.target ? (s.actual / s.target) * 100 : 0;
                  return (
                    <div key={i} className="flex items-center gap-3" data-testid={`sales-row-${s.period}`}>
                      <div className="text-xs font-mono text-slate-500 w-16">{s.period}</div>
                      <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden relative">
                        <div
                          className={`h-full ${pct >= 100 ? "bg-emerald-500" : pct >= 85 ? "bg-sky-500" : "bg-amber-500"}`}
                          style={{ width: `${Math.min(pct, 130)}%` }}
                        />
                        <div className="absolute top-0 bottom-0 left-[100%] w-px bg-slate-900" />
                      </div>
                      <div className="text-xs text-slate-600 w-12 text-right font-mono">%{pct.toFixed(0)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="training-panel">
            <div className="flex items-center gap-2 mb-4">
              <GraduationCap className="w-4 h-4 text-violet-600" />
              <h3 className="font-display font-semibold text-slate-900">Eğitim Geçmişi</h3>
              <span className="ml-auto text-xs text-slate-500">{training.length} eğitim</span>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {training.slice(0, 12).map((t, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded border border-slate-100 text-xs" data-testid={`training-row-${i}`}>
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${t.status === "Tamamlandı" ? "bg-emerald-500" : t.status === "Devam Ediyor" ? "bg-amber-500" : "bg-slate-300"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 truncate">{t.name}</div>
                    <div className="text-[0.65rem] text-slate-500">{t.category} · {t.duration_hours}h</div>
                  </div>
                  {t.score && <span className="font-mono text-slate-600">{t.score}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Yetkinlik profili özet */}
        {emp.current_skills?.length ? (
          <div className="bg-white border border-slate-200 rounded-xl p-6 mt-6" data-testid="skills-profile">
            <div className="flex items-center gap-2 mb-4">
              <Briefcase className="w-4 h-4 text-sky-600" />
              <h3 className="font-display font-semibold text-slate-900">Yetkinlik Profili</h3>
              <span className="ml-auto text-xs text-slate-500">{emp.current_skills.length} yetkinlik</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {emp.current_skills.map((s) => (
                <div key={s.skill_id} className="flex items-center gap-3" data-testid={`emp-skill-${s.skill_id}`}>
                  <span className="font-mono text-[0.6rem] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 flex-shrink-0">{s.skill_id}</span>
                  <span className="text-sm text-slate-800 truncate flex-1">{s.skill_name}</span>
                  <div className="flex gap-0.5 flex-shrink-0">
                    {[1, 2, 3, 4, 5].map((l) => (
                      <div key={l} className={`w-3 h-3 rounded-sm ${l <= s.level ? "bg-sky-500" : "bg-slate-200"}`} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
